import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { User, MapPin, Calendar, Trophy, Video, BookOpen, Twitter, Instagram, ArrowLeft, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicProfile = () => {
  const { shareSlug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile/${shareSlug}`);
        setProfile(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Profile not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [shareSlug]);

  if (loading) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Loading profile...</p>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h1 className="heading-1" style={{ marginBottom: '16px' }}>Profile Not Found</h1>
            <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>{error}</p>
            <Link to="/" className="btn-primary">Go Home</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <section style={{ 
        padding: '32px var(--spacing-medium)', 
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <Link to="/" style={{ color: 'var(--text-light)', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px', textDecoration: 'none' }}>
            <ArrowLeft size={18} /> Back to Pacific Ghost Baseball
          </Link>
          
          <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Profile Photo */}
            <div
              data-testid={profile.is_premium ? 'public-profile-premium-ring' : 'public-profile-avatar'}
              title={profile.is_premium ? 'Premium member' : undefined}
              style={{
                width: '180px',
                height: '180px',
                borderRadius: '16px',
                background: profile.photo_url ? 'none' : 'var(--bg-tertiary)',
                border: profile.is_premium ? '4px solid var(--brand-primary)' : '3px solid var(--border-medium)',
                boxShadow: profile.is_premium ? '0 0 0 2px rgba(0,0,0,0.4), 0 0 24px rgba(212,175,55,0.55)' : 'none',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <User size={80} style={{ color: 'var(--text-light)' }} />
              )}
            </div>

            {/* Basic Info */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h1 className="display-large" style={{ margin: 0 }}>{profile.name}</h1>
                {profile.is_premium && (
                  <span
                    data-testid="public-profile-premium-badge"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      borderRadius: '999px',
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.25), rgba(212,175,55,0.08))',
                      border: '1px solid var(--brand-primary)',
                      color: 'var(--brand-primary)',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Crown size={14} /> Premium Member
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {profile.position && (
                  <span style={{ 
                    padding: '6px 16px', 
                    background: 'var(--brand-primary)', 
                    color: 'black', 
                    borderRadius: '20px',
                    fontWeight: 700
                  }}>
                    {profile.position}
                  </span>
                )}
                {profile.team && (
                  <span style={{ color: 'var(--text-light)' }}>{profile.team}</span>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                {profile.height && <span><strong>Height:</strong> {profile.height}</span>}
                {profile.weight && <span><strong>Weight:</strong> {profile.weight}</span>}
                {profile.academic_info?.graduation_year && (
                  <span><strong>Class of:</strong> {profile.academic_info.graduation_year}</span>
                )}
                {profile.academic_info?.high_school && (
                  <span><strong>School:</strong> {profile.academic_info.high_school}</span>
                )}
              </div>

              {/* Social Links */}
              {(profile.twitter_handle || profile.instagram_handle) && (
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  {profile.twitter_handle && (
                    <a href={`https://twitter.com/${profile.twitter_handle}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)' }}>
                      <Twitter size={24} />
                    </a>
                  )}
                  {profile.instagram_handle && (
                    <a href={`https://instagram.com/${profile.instagram_handle}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)' }}>
                      <Instagram size={24} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      {profile.bio && (
        <section style={{ padding: 'var(--spacing-lg) var(--spacing-medium)' }}>
          <div className="container">
            <div className="network-card" style={{ padding: '24px' }}>
              <h2 className="heading-2" style={{ marginBottom: '16px' }}>About</h2>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{profile.bio}</p>
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section style={{ padding: 'var(--spacing-lg) var(--spacing-medium)', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Trophy size={28} style={{ color: 'var(--brand-primary)' }} />
            Performance Stats
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            {[
              { key: 'sixty_yard_time', label: '60 Yard Time' },
              { key: 'home_to_first', label: 'Home to 1st' },
              { key: 'velo', label: 'Velo' },
              { key: 'exit_velo', label: 'Exit Velo' },
              { key: 'broad_jump', label: 'Broad Jump' },
              { key: 'vertical_jump', label: 'Vertical Jump' }
            ].filter(stat => profile[stat.key]).map(stat => (
              <div key={stat.key} className="network-card" style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '8px' }}>{stat.label}</div>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{profile[stat.key]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Info */}
      {profile.academic_info && Object.values(profile.academic_info).some(v => v) && (
        <section style={{ padding: 'var(--spacing-lg) var(--spacing-medium)' }}>
          <div className="container">
            <h2 className="heading-1" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <BookOpen size={28} style={{ color: 'var(--brand-primary)' }} />
              Academic Information
            </h2>
            <div className="network-card" style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                {profile.academic_info.high_school && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>High School</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.high_school}</div>
                  </div>
                )}
                {profile.academic_info.graduation_year && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>Graduation Year</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.graduation_year}</div>
                  </div>
                )}
                {profile.academic_info.gpa && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>GPA</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.gpa}</div>
                  </div>
                )}
                {profile.academic_info.sat_score && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>SAT Score</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.sat_score}</div>
                  </div>
                )}
                {profile.academic_info.act_score && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>ACT Score</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.act_score}</div>
                  </div>
                )}
                {profile.academic_info.intended_major && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '4px' }}>Intended Major</div>
                    <div style={{ fontWeight: 600 }}>{profile.academic_info.intended_major}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Game Videos */}
      {profile.game_videos && profile.game_videos.length > 0 && (
        <section style={{ padding: 'var(--spacing-lg) var(--spacing-medium)', background: 'var(--bg-secondary)' }}>
          <div className="container">
            <h2 className="heading-1" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Video size={28} style={{ color: 'var(--brand-primary)' }} />
              Game Videos
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              {profile.game_videos.map((video, idx) => {
                // Handle both old format (string URL) and new format (object with title/url)
                const videoUrl = typeof video === 'string' ? video : video.url;
                const videoTitle = typeof video === 'string' ? null : video.title;
                
                return (
                  <div key={idx} className="network-card" style={{ padding: '0', overflow: 'hidden' }}>
                    <video src={videoUrl} controls style={{ width: '100%', display: 'block' }} />
                    {videoTitle && (
                      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-light)' }}>
                        <span style={{ fontWeight: 600 }}>{videoTitle}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Highlight Reel */}
      {profile.highlight_reel_url && (
        <section style={{ padding: 'var(--spacing-lg) var(--spacing-medium)' }}>
          <div className="container">
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Highlight Reel</h2>
            <div className="network-card" style={{ padding: '0', overflow: 'hidden', maxWidth: '800px', margin: '0 auto' }}>
              <video src={profile.highlight_reel_url} controls style={{ width: '100%', display: 'block' }} />
            </div>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)', textAlign: 'center', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <p style={{ color: 'var(--text-light)', marginBottom: '16px' }}>
            Powered by Pacific Ghost Baseball
          </p>
          <Link to="/contact" className="btn-primary">
            Contact Us About This Player
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PublicProfile;
